import React from "react";

const PrivacyPolicy = ({
  companyName = "Oak Crew MB",
  supportEmail = "support@oncodiary.com",
  infoEmail = "info@oncodiary.com",
  lastUpdated = "July 12, 2025"
}) => (
  <main style={{ padding: "2rem 1rem", maxWidth: "900px", margin: "0 auto" }}>
    <h1>Privacy Policy</h1>
    <p><strong>Last Updated:</strong> {lastUpdated}</p>

    <h2>1. Introduction</h2>
    <p>
      This Privacy Policy explains how {companyName} ("we," "our," or "us") collects, uses, and protects your information when you use our meal plan subscription service (the "Service"). By using the Service, you consent to the practices described herein. We only collect data necessary for the purposes outlined below.
    </p>

    <h2>2. Information We Collect</h2>
    <h3>2.1 Information You Provide</h3>
    <ul>
      <li>Account information: Email, password</li>
      <li>Profile details: Name, age, optional demographic data</li>
      <li>Dietary preferences and restrictions</li>
      <li>Meal plan preferences and goals</li>
      <li>Community posts &amp; interactions (if applicable)</li>
      <li>Payment information (processed by secure third-party providers)</li>
      <li>Feedback and support communications</li>
    </ul>

    <h3>2.2 Automatically Collected Information</h3>
    <ul>
      <li>Device information (model, OS, app version)</li>
      <li>Usage data (meal plan interactions, recipe views, app navigation)</li>
      <li>Log data (IP address, timestamps, session duration)</li>
      <li>Cookies &amp; tracking technologies for analytics and functionality</li>
    </ul>

    <h2>3. How We Use Your Information</h2>
    <p>We process your data to:</p>
    <ul>
      <li>Provide and improve the meal plan Service</li>
      <li>Personalize your meal recommendations and dietary experience</li>
      <li>Process payments and manage subscriptions</li>
      <li>Analyze usage patterns to enhance features and content</li>
      <li>Maintain security and prevent fraud</li>
      <li>Send service updates and marketing communications (with opt-out options)</li>
      <li>Provide customer support and respond to inquiries</li>
    </ul>
    <p>
      <strong>Legal Basis:</strong> We process data based on contractual necessity (Service delivery), consent (dietary data, marketing), legitimate interest (analytics, security), or legal obligations (tax compliance).
    </p>

    <h2>4. Data Sharing and Disclosure</h2>
    <h3>4.1 Service Providers</h3>
    <p>We share data with trusted service providers:</p>
    <ul>
      <li>Payment processors (Stripe, Apple Pay, Google Pay) for secure transactions</li>
      <li>Cloud hosting services for data storage and app functionality</li>
      <li>Analytics providers for usage insights and app improvement</li>
      <li>Email service providers for communications</li>
    </ul>
    <p>These providers are bound by strict data security agreements and can only use your data for specified purposes.</p>

    <h3>4.2 Other Disclosures</h3>
    <p>We may disclose information for:</p>
    <ul>
      <li>Legal compliance or fraud prevention</li>
      <li>Protection of users' rights, privacy, safety, or property</li>
      <li>Business transfers (e.g., mergers), with advance notice where required</li>
      <li>Anonymized data for research and analytics</li>
    </ul>

    <h2>5. Subscription &amp; Billing Information</h2>
    <p>
      Our monthly subscription ($9.99 USD) auto-renews unless canceled by contacting <a href={`mailto:${supportEmail}`}>{supportEmail}</a>. 
      Some plans include a 7-day free trial. Payments are processed by secure third-party providers—we do not store your payment card details.
    </p>

    <h2>6. Data Retention</h2>
    <ul>
      <li><strong>Account/profile data:</strong> Kept while your account is active, up to 90 days in backups post-deletion</li>
      <li><strong>Payment data:</strong> Retained as needed for transactions and legal obligations</li>
      <li><strong>Usage data:</strong> Kept anonymized indefinitely for service improvement</li>
      <li><strong>Dietary preferences:</strong> Retained to maintain personalized meal plans until account deletion</li>
    </ul>

    <h2>7. Your Rights (GDPR &amp; CCPA)</h2>
    <p>You have the right to:</p>
    <ul>
      <li>Access, correct, or delete your personal data</li>
      <li>Object to processing (e.g., marketing communications)</li>
      <li>Export your data in a portable format</li>
      <li>Opt out of data "sales" (we don't sell data but share with analytics partners)</li>
      <li>Lodge a complaint with a data protection authority</li>
    </ul>
    <p>To exercise these rights, contact <a href={`mailto:${infoEmail}`}>{infoEmail}</a>.</p>

    <h2>8. Security Measures</h2>
    <p>We implement industry-standard security measures:</p>
    <ul>
      <li>Encryption for sensitive data transmission and storage</li>
      <li>Secure payment processing through certified providers</li>
      <li>Regular security audits and monitoring</li>
      <li>Access controls to limit data access to authorized personnel</li>
      <li>Breach notification within 72 hours if required by law</li>
    </ul>

    <h2>9. International Data Transfers</h2>
    <p>
      Data may be processed outside Lithuania with appropriate safeguards, such as EU Standard Contractual Clauses, to ensure your data remains protected.
    </p>

    <h2>10. Children's Privacy</h2>
    <p>
      The Service is designed for users 18 years and older. We do not knowingly collect personal information from children under 18. If you believe a child has provided us with personal information, please contact us immediately.
    </p>

    <h2>11. Marketing Communications &amp; Tracking</h2>
    <p>
      We use cookies and tracking technologies for functionality, analytics, and marketing purposes. You can manage your preferences in your account settings or unsubscribe from marketing emails at any time using the unsubscribe link.
    </p>

    <h2>12. Mobile App Privacy</h2>
    <p>Our mobile app may request optional permissions for:</p>
    <ul>
      <li>Push notifications for meal reminders and updates</li>
      <li>Camera access for meal photo features (if applicable)</li>
      <li>Location services for local ingredient availability (if applicable)</li>
    </ul>
    <p>You can revoke these permissions at any time through your device settings.</p>

    <h2>13. Changes to Privacy Policy</h2>
    <p>
      We may update this Privacy Policy periodically. Material changes will be communicated via email or in-app notification at least 30 days before taking effect. Continued use of the Service after changes constitutes acceptance of the updated policy.
    </p>

    <h2>14. Contact Us</h2>
    <p>
      For questions about this Privacy Policy or to exercise your data rights:
    </p>
    <ul>
      <li><strong>Email (General):</strong> <a href={`mailto:${infoEmail}`}>{infoEmail}</a></li>
      <li><strong>Email (Support):</strong> <a href={`mailto:${supportEmail}`}>{supportEmail}</a></li>
    </ul>
  </main>
);

export default PrivacyPolicy;
